/* eslint-disable no-var */
import React, { useEffect, useReducer } from 'react';
import clsx from 'clsx';
import { isDesktop } from 'react-device-detect';
import {
  VideoStreamState,
  reducer,
  setCurrentPhotoBlob,
  setInitialData,
  setTorchStatus,
  setIsBackCamera,
  startCapturing,
  setQualityThreshold,
} from './reducer';
import {
  getCameras,
  getVideoCapabilities,
  initVideoStream,
  stopCamera,
  takeAPhoto,
  takeTheBestPhotoWithOpenCV,
} from './video.utils';
import CircleLoader from 'components/CircleLoader';
import VideoControls from './VideoControls';
import RotateMobileMessage from './RotateMobileMessage';
import styles from './index.module.scss';
import FocusQualityIndicator from './FocusQualityIndicator';
import VideoStreamTarget from './VideoStreamTarget';
import { useFeatureFlags } from '../../contexts/featureFlag/useFeatureFlags';

export interface VideoStreamProps {
  onImageCapture?: (
    base64Image: string,
    focusMeasure: number,
    acceptanceThreshold: number,
    isBlankImage?: boolean,
    isWhiteImage?: boolean
  ) => void;
  showTestPreview?: boolean;
  disableCameraButton?: boolean;
  aspectRatio?: 'document' | 'card' | 'square';
  withTarget?: boolean;
  className?: string;
  hide?: boolean;
  paused?: boolean;
  useMultiplePhotos?: boolean;
  calculateBlurScore?: boolean;
  onMediaTrackChanged?: (mediaTrack: MediaStreamTrack | undefined) => void;
}

function VideoStream(props: VideoStreamProps) {
  const { featureFlags } = useFeatureFlags();
  const [state, dispatch] = useReducer(reducer, {
    isBackCamera: true,
    isLoading: true,
  });

  useEffect(() => {
    (async () => {
      const cameras = await getCameras();
      const defaultCamera = cameras.backCamera || cameras.cameras[0];
      const stream = await initVideoStream(state.isBackCamera, 'video');

      const capabilities = await getVideoCapabilities(stream.track);

      const videoInitialData: VideoStreamState = {
        isBackCamera: state.isBackCamera,
        mediaTrack: stream.track,
        videoStream: stream.videoStream,
        currentCamera: defaultCamera,
        videoPlayer: stream.videoPlayer,
        capabilities: {
          mainCamera: cameras.backCamera || cameras.cameras[0],
          frontCamera: cameras.frontCamera,
          isTorchSupported: capabilities.isTorchSupported,
          imageHeight: capabilities.imageHeight,
          imageWidth: capabilities.imageWidth,
        },
      };

      dispatch(setInitialData(videoInitialData));
    })();
  }, [state.isBackCamera]);

  useEffect(() => {
    if (props.onMediaTrackChanged) {
      props.onMediaTrackChanged(state.mediaTrack);
    }
  }, [state.mediaTrack]);

  const onTakePhoto = async () => {
    dispatch(startCapturing());

    const {
      image: croppedImage,
      focusQuality,
      isBlankImage,
      isWhiteImage,
    } = props.useMultiplePhotos
      ? await takeTheBestPhotoWithOpenCV(
          state.videoPlayer!,
          state.mediaTrack!.getSettings().width!,
          state.mediaTrack!.getSettings().height!,
          featureFlags['video-stream-time-between-images'],
          featureFlags['video-stream-images-count']
        )
      : await takeAPhoto(
          state.videoPlayer!,
          state.mediaTrack!.getSettings().width!,
          state.mediaTrack!.getSettings().height!,
          false,
          props.calculateBlurScore
        );

    console.log('>>> Image taken: ', {
      videoStreamTimeBetweenImages:
        featureFlags['video-stream-time-between-images'],
      videoStreamImagesCount: featureFlags['video-stream-images-count'],
      focusQuality,
    });

    dispatch(setCurrentPhotoBlob(croppedImage, focusQuality));

    props.onImageCapture?.(
      croppedImage,
      focusQuality!,
      state.focusQualityThreshold!,
      isBlankImage,
      isWhiteImage
    );
  };

  useEffect(() => {
    if (state.videoPlayer) {
      takeAPhoto(
        state.videoPlayer!,
        state.mediaTrack!.getSettings().width!,
        state.mediaTrack!.getSettings().height!
      );
    }
  }, [state.videoStream]);

  useEffect(() => {
    return () => {
      if (state.videoStream) {
        stopCamera(state.videoStream);
      }
    };
  }, [state.videoStream]);

  useEffect(() => {
    if (!state.videoPlayer) return;
    if (props.paused) {
      state.videoPlayer?.pause();
    } else {
      state.videoPlayer?.play();
    }
  }, [props.paused]);

  const onFlashlightChange = () => {
    const { mediaTrack, isTorchOn } = state;
    if (!mediaTrack) return;
    mediaTrack.applyConstraints({
      advanced: [{ torch: !isTorchOn } as any],
    });
    dispatch(setTorchStatus(!isTorchOn));
  };

  const onDownloadPhoto = () => {
    var link = document.createElement('a');
    link.href = state.currentPhotoUrl!;
    link.setAttribute('download', `photo-haul_${Date.now()}.png`);
    link.click();
  };

  const onContinue = () => {
    dispatch(setCurrentPhotoBlob(undefined));
  };

  const onCapture = () => {
    onTakePhoto();
  };

  const onFlipCamera = () => {
    dispatch(setIsBackCamera(!state.isBackCamera));
  };

  const onNewThreshold = (threshold: number) => {
    dispatch(setQualityThreshold(threshold));
  };

  return (
    <>
      {!isDesktop && <RotateMobileMessage />}
      <div
        className={clsx(props.className, {
          [styles.hide]: props.hide,
        })}
      >
        {state.isLoading && <CircleLoader />}
        <div
          className={clsx({
            [styles.videoHidden]:
              state.isLoading || (props.showTestPreview && state.isPreview),
          })}
        >
          <div className={clsx(styles.videoStreamContainer)}>
            <video
              className={clsx(
                styles.video,
                styles[`aspectRatio_${props.aspectRatio}`]
              )}
              id="video"
              width={state.videoPlayer?.offsetWidth || 480}
              height={state.videoPlayer?.offsetHeight || 480}
              autoPlay={true}
              playsInline={true}
              muted={true}
              {...{
                ['x-webkit-airplay']: 'deny',
              }}
            />
            {props.withTarget && (
              <VideoStreamTarget isCapturing={state.isCapturing} />
            )}
          </div>
          {!state.isCapturing && props.calculateBlurScore && (
            <FocusQualityIndicator
              video={state.videoPlayer}
              thresholdPercentage={
                Number.isFinite(Number(featureFlags['threshold-percentage']))
                  ? Number(featureFlags['threshold-percentage'])
                  : undefined
              }
              onNewThreshold={onNewThreshold}
              hide
            />
          )}
          <VideoControls
            flashOn={state.isTorchOn}
            disabledCamera={
              props.disableCameraButton ||
              state.isCapturing ||
              (!state.focusQualityThreshold && props.calculateBlurScore)
            }
            onCameraClick={onCapture}
            onFlashClick={onFlashlightChange}
            onFlipClick={onFlipCamera}
          />
        </div>
        {props.showTestPreview && state.isPreview && (
          <div>
            <img id="img" src={state.currentPhotoUrl} width={'100%'} />
            <div className={styles.testActions}>
              <button onClick={onDownloadPhoto}>Download photo</button>
              <button onClick={onContinue}>Continue</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default VideoStream;
